import { spokenAnswer } from './AnswerButton';
import type { SpeechItem } from '../services/tts/types';
import { useT } from '../hooks/useT';
import { useSettings } from '../state/SettingsContext';
import { useTts } from '../state/TtsContext';
import type { SpeechSpeed } from '../types';

interface AudioControlsProps {
  /** The full sequence for "Read Everything": question, then every answer. */
  items: SpeechItem[];
  /** Hide the speed buttons (used inside dense screens). */
  hideSpeed?: boolean;
}

const SPEED_ORDER: SpeechSpeed[] = ['slow', 'default', 'normal'];

/**
 * "Read Everything" plus replay / pause / resume / stop and the speed choice.
 * These are the controls a learner uses most, so they stay large and constant.
 */
export function AudioControls({ items, hideSpeed = false }: AudioControlsProps) {
  const t = useT();
  const { settings, update } = useSettings();
  const { speakSequence, pause, resume, stop, replay, status, canSpeakText } = useTts();

  // Enabled as long as something in the sequence can be said. Individual
  // phrases with no voice are skipped rather than blocking the whole reading.
  const anySpeakable = items.some((item) => canSpeakText(item.text, item.lang));

  const speaking = status.state === 'speaking';
  const paused = status.state === 'paused';
  const idle = status.state === 'idle';

  return (
    <div className="stack-sm">
      <div className="audio-bar" role="group" aria-label={t('readEverything')}>
        <button
          type="button"
          className="btn btn--primary btn--big"
          onClick={() => speakSequence(items)}
          disabled={!anySpeakable || items.length === 0}
        >
          <span className="btn__icon" aria-hidden="true">
            🔊
          </span>
          {t('readEverything')}
        </button>

        {speaking ? (
          <button type="button" className="btn" onClick={pause}>
            <span className="btn__icon" aria-hidden="true">
              ⏸
            </span>
            {t('pause')}
          </button>
        ) : null}

        {paused ? (
          <button type="button" className="btn" onClick={resume}>
            <span className="btn__icon" aria-hidden="true">
              ▶
            </span>
            {t('resume')}
          </button>
        ) : null}

        <button type="button" className="btn" onClick={stop} disabled={idle}>
          <span className="btn__icon" aria-hidden="true">
            ⏹
          </span>
          {t('stop')}
        </button>

        <button type="button" className="btn" onClick={replay} disabled={!anySpeakable}>
          <span className="btn__icon" aria-hidden="true">
            🔁
          </span>
          {t('replay')}
        </button>
      </div>

      {hideSpeed ? null : settings.easyMode ? (
        <div className="audio-bar" role="group" aria-label={t('speechSpeed')}>
          <button
            type="button"
            className="choice"
            aria-pressed={settings.speechSpeed === 'slow'}
            onClick={() => update({ speechSpeed: settings.speechSpeed === 'slow' ? 'default' : 'slow' })}
          >
            <span aria-hidden="true">🐢 </span>
            {t('slower')}
          </button>
        </div>
      ) : (
        <div className="choice-row" role="group" aria-label={t('speechSpeed')}>
          {SPEED_ORDER.map((speed) => (
            <button
              key={speed}
              type="button"
              className="choice"
              aria-pressed={settings.speechSpeed === speed}
              onClick={() => update({ speechSpeed: speed })}
            >
              {speed === 'slow' ? t('speedSlow') : speed === 'default' ? t('speedDefault') : t('speedNormal')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Builds the "Read Everything" sequence for a question and its answers. */
export function buildQuestionSequence(
  questionText: string,
  answers: string[],
  lang: 'ar' | 'en',
): SpeechItem[] {
  return [
    { id: 'question', text: questionText, lang, gapAfterMs: 900 },
    ...answers.map((answer, index) => ({
      id: `answer-${index}`,
      // spokenAnswer, not the visible chip: the letter is said by name.
      text: spokenAnswer(index, answer, lang),
      lang,
      gapAfterMs: 750,
    })),
  ];
}
